import { Button } from '@components/_ui/Button'
import { Counter } from '@components/_ui/Counter'
import { ExplanationText } from '@components/_ui/ExplanationText'
import { Grid } from '@components/_ui/Grid'
import { Loader } from '@components/_ui/Loader'
import { MotionWrapper } from '@components/_ui/MotionWrapper'
import { Question } from '@components/_ui/Question'
import { useFetch } from '@hooks/useFetch'
import { usePriceSetter } from '@hooks/usePriceSetter'
import { useScrollToNewQuestion } from '@hooks/useScrollToNewQuestion'
import { useSteps } from '@hooks/useSteps'
import { transformFormData } from '@lib/transformFormData'
import { unregisterUnusedFields } from '@lib/unregisterUnusedFields'
import { QuestionType } from '@types'
import { AnimatePresence } from 'motion/react'
import { ReactNode, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import s from './Form.module.scss'
import type { FormProps } from './Form.props'

export function Form({ data }: FormProps) {
  const { register, setValue, unregister } = useFormContext()

  const requestedCleaningType = useWatch({ name: 'requested_cleaning_type' })
  const homeType = useWatch({ name: 'home_type' })
  const isBasementCleaning = useWatch({ name: 'is_basement_cleaning' })
  const bedBasement = useWatch({ name: 'bed_basement' })
  const bathBasement = useWatch({ name: 'bath_basement' })
  const bed = useWatch({ name: 'bed' })
  const bath = useWatch({ name: 'bath' })

  const questions = useMemo(() =>
    data
      .filter((question) => {
        if (['last_time_cleaned', 'home_state'].includes(question.db_field_name)) {
          return requestedCleaningType === 'basic_deep_cleaning'
        } else if (!homeType) {
          return question.db_field_name === 'home_type'
        } else if (question.db_field_name === 'is_basement_cleaning') {
          // show is_basement_cleaning if home_type = townhouse || house
          return ['townhouse', 'house'].includes(homeType)
        } else if (['bed_basement', 'bath_basement'].includes(question.db_field_name)) {
          // show bed_basement or bath_basement if is_basement_cleaning = yes && home_type = townhouse || house
          return isBasementCleaning === 'true' && ['townhouse', 'house'].includes(homeType)
        } else {
          return true
        }
      })
      .map((question) => {
        if (question.db_field_name === 'bed') {
          // set min max for bed field by home_type
          return ({
            ...question,
            ...(question?.options?.length && {
              options: question.options.map((option) => ({
                ...option,
                min: homeType === 'condo_apartment' ? '0' : '2',
                max: homeType === 'condo_apartment' ? '3' : '6'
              }))
            })
          })
        } else {
          return question
        }
      })
  , [homeType, isBasementCleaning, bedBasement])

  const questionRefs = useScrollToNewQuestion(questions)

  useEffect(() => {
    unregisterUnusedFields({ initialData: data, usedData: questions, unregister })
  }, [questions])

  return (
    <div className={s.form}>
      <AnimatePresence initial={false}>
        {questions?.length && questions.map((question, index) => (
          <MotionWrapper
            key={question.db_field_name}
            ref={(el) => (questionRefs.current[question.db_field_name] = el)}
          >
            <Question
              question={question.question}
              info={question?.explanation?.content}
              index={index + 1}
            />

            <ExplanationText data={question.explanation} />

            {({
              single: (
                <Grid
                  options={question.options}
                  {...register(question.db_field_name)}
                />
              ),
              numeric: (
                <Counter
                  min={Number(question?.options?.[0]?.min)}
                  max={Number(question?.options?.[0]?.max)}
                  initValue={{
                    bed_basement: bedBasement ?? Number(question?.options?.[0]?.min),
                    bath_basement: bathBasement ?? Number(question?.options?.[0]?.min),
                    bed: bed ?? (homeType === 'condo_apartment' ? 1 : 3),
                    bath: bath ?? Number(question?.options?.[0]?.min),
                  }[question.db_field_name] ?? Number(question?.options?.[0]?.min)}
                  icon={question?.options?.[0]?.image}
                  onChange={(value) => setValue(question.db_field_name, value)}
                  text={question?.options?.[0]?.item}
                />
              )
            } as Record<QuestionType, ReactNode>)[question.question_type]}
          </MotionWrapper>
        ))}
      </AnimatePresence>
    </div>
  )
}

function FormButton({ data }: FormProps) {
  const { handleSubmit } = useFormContext()
  const { trigger, isLoading } = useFetch()
  const { stepsHistory, setStepsHistory, setCurrentStep } = useSteps()
  const setPriceData = usePriceSetter()

  const requestedCleaningType = useWatch({ name: 'requested_cleaning_type' })
  const [firstValue, secondValue] = useWatch({ name: ['last_time_cleaned', 'home_state'] })
  const homeType = useWatch({ name: 'home_type' })
  const isBasementCleaning = useWatch({ name: 'is_basement_cleaning' })

  const disabled = 
    (requestedCleaningType === 'basic_deep_cleaning' && (!firstValue || !secondValue)) || 
    !homeType || 
    (homeType !== 'condo_apartment' && !isBasementCleaning)

  const nextHandler = handleSubmit((formData) => {
    if (disabled) return

    const nextStep = 3
    const newHistory = [...stepsHistory, 3]

    const transformedFormData = transformFormData(data, formData)

    trigger({ ...transformedFormData, current_screen: nextStep, history: newHistory })
      .then((res) => {
        setPriceData((prev) => ({ ...prev, ...res?.data?.data }))
        setCurrentStep(nextStep)
        setStepsHistory(newHistory)
      })
  })

  return (
    <Button
      onClick={nextHandler}
      disabled={isLoading || disabled}
    >
      {isLoading ? <Loader /> : 'Next'}
    </Button>
  )
}

Form.Button = FormButton
